import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import {
  SMS_SIGNUP_CONSENT_VERSION, SMS_PROMO_CONSENT_VERSION, SMS_TRANSACTIONAL_CONSENT_TEXT, SMS_PROMO_CONSENT_TEXT,
  COUNTRIES, STATES_BY_COUNTRY,
} from "@/lib/constants";

const ConsentBox = ({ id, label, checked, onChange, children }) => (
  <div className="flex items-start gap-2.5">
    <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)}
      className="mt-0.5 data-[state=checked]:bg-olive data-[state=checked]:border-olive"
      data-testid={`signup-${id.replace(/_/g, "-")}`} />
    <Label htmlFor={id} className="text-xs leading-relaxed font-normal text-espresso-soft cursor-pointer">
      <span className="block font-semibold text-espresso mb-0.5">{label}</span>
      {children}
    </Label>
  </div>
);

export default function Signup() {
  const [form, setForm] = useState({ full_name: "", email: "", password: "", company: "" });
  const [country, setCountry] = useState("US");
  const [stateVal, setStateVal] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [promoConsent, setPromoConsent] = useState(false);
  const [smsPhone, setSmsPhone] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const formatPhone = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!agreeTerms) {
      setError("Please agree to the Terms of Service, Privacy Policy, and SMS Terms to continue.");
      return;
    }
    const wantsSms = smsConsent || promoConsent;
    if (wantsSms && smsPhone.replace(/\D/g, "").length !== 10) {
      setError("Please enter a valid 10-digit mobile number to receive text messages, or uncheck the text message boxes.");
      return;
    }
    setLoading(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      const { data } = await api.post("/auth/register", {
        ...form,
        timezone: tz,
        accept_terms: true,
        country,
        state: stateVal || null,
        sms_consent: smsConsent,
        sms_promo_consent: promoConsent,
        sms_phone: wantsSms ? smsPhone : null,
        sms_consent_version: smsConsent ? SMS_SIGNUP_CONSENT_VERSION : null,
        sms_promo_consent_version: promoConsent ? SMS_PROMO_CONSENT_VERSION : null,
      });
      navigate("/verify-email", { state: { email: form.email, devCode: data.dev_code } });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthShell title="Start tracking" subtitle="Create your PaySignal account. No SMS required."
      footer={<>Already have an account? <Link to="/login" className="text-olive font-semibold underline" data-testid="link-login">Log in</Link></>}>
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="signup-error">{error}</div>}
        <div className="space-y-1.5">
          <Label htmlFor="full_name">Full name</Label>
          <Input id="full_name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required data-testid="signup-name" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="signup-email" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} data-testid="signup-password" />
          <p className="text-xs text-warmmuted">At least 8 characters.</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="company">Company name <span className="text-xs font-normal text-warmmuted">(optional)</span></Label>
          <Input id="company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="signup-company" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Country</Label>
            <Select value={country} onValueChange={(v) => { setCountry(v); setStateVal(""); }}>
              <SelectTrigger data-testid="signup-country"><SelectValue /></SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code} data-testid={`country-${c.code}`}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{country === "CA" ? "Province" : "State"}</Label>
            <Select value={stateVal} onValueChange={setStateVal}>
              <SelectTrigger data-testid="signup-state"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {(STATES_BY_COUNTRY[country] || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Optional SMS consents — each unchecked by default, separate from Terms (A2P 10DLC) */}
        <div className="rounded-2xl border border-warmborder bg-oatmeal/50 p-4 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="sms_phone" className="flex items-center gap-2">
              Mobile number <span className="text-xs font-normal text-warmmuted">(optional)</span>
            </Label>
            <Input id="sms_phone" type="tel" inputMode="tel" value={smsPhone}
              onChange={(e) => setSmsPhone(formatPhone(e.target.value))}
              placeholder="(555) 012-3456" data-testid="signup-sms-phone" />
          </div>
          <ConsentBox id="sms_consent" label="Payment alerts & account notifications (optional)"
            checked={smsConsent} onChange={setSmsConsent}>
            {SMS_TRANSACTIONAL_CONSENT_TEXT}
          </ConsentBox>
          <ConsentBox id="sms_promo_consent" label="Promotional messages (optional)"
            checked={promoConsent} onChange={setPromoConsent}>
            {SMS_PROMO_CONSENT_TEXT}
          </ConsentBox>
        </div>

        {/* Required agreement — separate from SMS consent */}
        <div className="flex items-start gap-2.5">
          <Checkbox id="agree_terms" checked={agreeTerms}
            onCheckedChange={(v) => setAgreeTerms(v === true)}
            className="mt-0.5 data-[state=checked]:bg-olive data-[state=checked]:border-olive"
            data-testid="signup-agree-terms" />
          <Label htmlFor="agree_terms" className="text-xs leading-relaxed font-normal text-espresso-soft cursor-pointer">
            I agree to the{" "}
            <Link to="/terms" target="_blank" className="underline font-medium text-espresso hover:text-olive" data-testid="signup-terms-link">Terms of Service</Link>,{" "}
            <Link to="/privacy" target="_blank" className="underline font-medium text-espresso hover:text-olive" data-testid="signup-privacy-link">Privacy Policy</Link>, and{" "}
            <Link to="/sms-terms" target="_blank" className="underline font-medium text-espresso hover:text-olive" data-testid="signup-sms-terms-link">SMS Terms</Link>.{" "}
            <span className="text-rose-600" aria-label="required">*</span>
          </Label>
        </div>

        <Button type="submit" className="w-full" disabled={loading || !agreeTerms} data-testid="signup-submit">
          {loading ? "Creating…" : "Create account"}
        </Button>
      </form>
      <div className="relative my-6 text-center">
        <span className="bg-background px-3 text-xs text-warmmuted relative z-10">OR</span>
        <div className="absolute inset-x-0 top-1/2 border-t border-warmborder" />
      </div>
      <Button variant="outline" className="w-full" onClick={google} data-testid="google-signup">Continue with Google</Button>
    </AuthShell>
  );
}
